/**
 * BulkAddToSessionModal — put every selected item into one session's pool.
 * Only sessions that can still take items are offered: a published session
 * is immutable and a locked configuration no longer changes its pool. The
 * add goes through sessionStore.addItems, one item at a time, which reports
 * which items the session took and which it refused.
 */
import { useMemo, useState } from 'react';
import { Modal, TextInput, Radio, Callout, EmptyState } from '@/components/ds';
import { Button } from '@/components/ui/Button';
import { useSessionStore } from '../../../stores/prioritization/sessionStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { itemsWord, type BulkActionResult } from './index';

export interface BulkAddToSessionModalProps {
  open: boolean;
  selectedIds: string[];
  onClose: () => void;
  onComplete: (result: BulkActionResult) => void;
}

const STATE_WORD: Record<string, string> = {
  DRAFT: 'Draft',
  CRITERIA_FINALIZED: 'Criteria set',
  RUN_COMPLETE: 'Run complete',
  PUBLISHED: 'Published',
};

export function BulkAddToSessionModal({ open, selectedIds, onClose, onComplete }: BulkAddToSessionModalProps) {
  const [query, setQuery] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const sessions = useSessionStore((s) => s.sessions);
  const addItems = useSessionStore((s) => s.addItems);
  const items = useItemsStore((s) => s.items);

  const openSessions = useMemo(
    () =>
      sessions
        .filter((s) => s.state !== 'PUBLISHED' && !s.configurationLockedAt)
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()),
    [sessions],
  );
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? openSessions.filter((s) => s.name.toLowerCase().includes(q)) : openSessions;
  }, [openSessions, query]);

  const chosen = openSessions.find((s) => s.id === sessionId) ?? null;
  const count = selectedIds.length;
  const alreadyIn = chosen ? selectedIds.filter((id) => (chosen.itemIds ?? []).includes(id)).length : 0;

  const apply = async () => {
    if (!chosen || busy) return;
    setBusy(true);
    try {
      const { added, failed } = await addItems(chosen.id, selectedIds);
      const failedIds = new Set(failed.map((f) => f.itemId));
      const succeededIds = selectedIds.filter((id) => !failedIds.has(id));
      void added;
      onComplete({
        action: `Added to ${chosen.name}`,
        total: count,
        succeeded: succeededIds.length,
        succeededIds,
        failed: failed.map((f) => ({
          itemId: f.itemId,
          itemTitle: items.find((i) => i.id === f.itemId)?.title ?? f.itemId,
          reason: f.reason,
        })),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => { if (!busy) onClose(); }}
      dismissible={!busy}
      size="md"
      title={`Add ${itemsWord(count)} to a session`}
      description="The items join the session's pool. Published sessions and locked configurations can't take items, so they are not listed."
      footer={
        <>
          <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="button" variant="cta" size="sm" onClick={() => void apply()} disabled={busy || !chosen || alreadyIn === count}>
            {busy ? 'Adding…' : chosen ? `Add ${itemsWord(count - alreadyIn)}` : 'Choose a session'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {openSessions.length === 0 ? (
          <EmptyState
            title="No session can take items right now"
            description="Every session is published or has a locked configuration. Create a session first, then add items to it."
          />
        ) : (
          <>
            {openSessions.length > 6 && (
              <TextInput
                size="sm"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search sessions"
                autoFocus
                disabled={busy}
              />
            )}
            <div className="max-h-64 overflow-y-auto border" style={{ borderColor: 'var(--rule)' }}>
              {shown.length === 0 && <EmptyState title="No session matches" />}
              {shown.map((s) => (
                <div key={s.id} className="border-b px-3 py-2 last:border-b-0" style={{ borderColor: 'var(--rule)' }}>
                  <Radio
                    name="bulk-session"
                    checked={s.id === sessionId}
                    onChange={() => setSessionId(s.id)}
                    disabled={busy}
                    label={s.name}
                    description={`${STATE_WORD[s.state] ?? s.state} · ${itemsWord((s.itemIds ?? []).length)} in the pool`}
                  />
                </div>
              ))}
            </div>
          </>
        )}
        {chosen && (
          <Callout tone="slate">
            {alreadyIn === count ? (
              <>All {itemsWord(count)} are already in <strong className="text-[var(--text-pri)]">{chosen.name}</strong>.</>
            ) : (
              <>
                You are about to add {itemsWord(count - alreadyIn)} to{' '}
                <strong className="text-[var(--text-pri)]">{chosen.name}</strong>.
                {alreadyIn > 0 && ` ${itemsWord(alreadyIn)} ${alreadyIn === 1 ? 'is' : 'are'} already in it and ${alreadyIn === 1 ? 'stays' : 'stay'} as ${alreadyIn === 1 ? 'it is' : 'they are'}.`}
              </>
            )}
          </Callout>
        )}
      </div>
    </Modal>
  );
}
